<template>
  <div class="copyright-page">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>版权申请</span>
        <el-select v-model="filterStatus" placeholder="全部状态" clearable style="width: 140px" @change="handleSearch">
          <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
        </el-select>
      </div>

      <el-table v-loading="loading" :data="list" stripe class="copyright-table">
        <el-table-column label="作品图片" width="90">
          <template slot-scope="{ row }">
            <div class="work-thumb">
              <img v-if="row.workCoverUrl" :src="row.workCoverUrl" alt="">
              <i v-else class="el-icon-picture-outline" />
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="workTitle" label="作品名称" min-width="160" show-overflow-tooltip />
        <el-table-column label="版权编号" min-width="130">
          <template slot-scope="{ row }">{{ row.copyrightNo || '-' }}</template>
        </el-table-column>
        <el-table-column label="申请人" width="130">
          <template slot-scope="{ row }">{{ row.applicantName || `用户${row.userId}` }}</template>
        </el-table-column>
        <el-table-column label="申请时间" width="120">
          <template slot-scope="{ row }">{{ formatUserDate(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="版权费用" width="100">
          <template slot-scope="{ row }">
            <span v-if="row.fee != null && row.fee > 0" class="fee">¥{{ row.fee }}</span>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="copyrightStatusTagType(row.status)">
              {{ labelOf(COPYRIGHT_STATUS, row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="320" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
            <template v-if="row.status === 0">
              <el-button size="mini" type="success" plain @click="approve(row)">通过</el-button>
              <el-button size="mini" type="danger" plain @click="openReject(row)">驳回</el-button>
            </template>
            <template v-else-if="row.status === 1">
              <el-button type="text" size="small" @click="openCertificate(row)">
                {{ row.certificateUrl ? '更换证书' : '上传证书' }}
              </el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="loadList"
      />
    </el-card>

    <el-dialog
      :visible.sync="detailVisible"
      width="640px"
      top="6vh"
      custom-class="copyright-detail-dialog"
      @closed="handleDetailClosed"
    >
      <div v-if="detail" slot="title" class="detail-title">
        <span class="detail-icon"><i class="el-icon-document-checked" /></span>
        <span>版权申请详情</span>
      </div>

      <div v-if="detail" v-loading="detailLoading" class="detail-body">
        <section class="detail-section">
          <div class="section-head">
            <span class="section-icon"><i class="el-icon-info" /></span>
            <span>基本信息</span>
          </div>
          <div class="field-grid">
            <div class="field-item">
              <span class="field-label">申请编号</span>
              <span class="field-value">{{ formatCopyrightCode(detail.id) }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">版权编号</span>
              <span class="field-value">{{ detail.copyrightNo || '-' }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">作品名称</span>
              <span class="field-value">{{ detail.workTitle || '-' }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">作品类型</span>
              <span class="field-value">{{ displayWorkType(detail.workType) }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">申请时间</span>
              <span class="field-value">{{ formatUserDate(detail.createdAt) }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">版权费用</span>
              <span class="field-value fee-value">
                <template v-if="detail.fee != null && detail.fee > 0">¥{{ detail.fee }}</template>
                <template v-else>-</template>
              </span>
            </div>
            <div class="field-item">
              <span class="field-label">审核状态</span>
              <span class="field-value">
                <el-tag size="mini" :type="auditStatusTagType(detail.status)">
                  {{ labelOf(COPYRIGHT_AUDIT_STATUS, detail.status) }}
                </el-tag>
              </span>
            </div>
            <div class="field-item">
              <span class="field-label">支付状态</span>
              <span class="field-value">
                <el-tag size="mini" :type="payStatusTagType(detail.status)">
                  {{ labelOf(COPYRIGHT_PAY_STATUS, detail.status) }}
                </el-tag>
              </span>
            </div>
          </div>
        </section>

        <section class="detail-section">
          <div class="section-head">
            <span class="section-icon"><i class="el-icon-film" /></span>
            <span>作品信息</span>
          </div>
          <div class="field-grid">
            <div class="field-item">
              <span class="field-label">作品来源</span>
              <span class="field-value">{{ detail.workSource === 2 ? '站外作品' : '站内作品' }}</span>
            </div>
            <div v-if="detail.workId" class="field-item">
              <span class="field-label">作品ID</span>
              <span class="field-value">{{ formatWorkCode(detail.workId) }}</span>
            </div>
            <div v-if="detail.workUrl" class="field-item field-full">
              <span class="field-label">作品地址</span>
              <span class="field-value">{{ detail.workUrl }}</span>
            </div>
          </div>
          <div class="desc-block">
            <div class="desc-label">作品简介</div>
            <div class="desc-content">{{ detail.workIntro || detail.description || '暂无简介' }}</div>
          </div>
        </section>

        <section class="detail-section">
          <div class="section-head">
            <span class="section-icon"><i class="el-icon-user" /></span>
            <span>作者信息</span>
          </div>
          <div class="field-grid">
            <div class="field-item">
              <span class="field-label">作者姓名</span>
              <span class="field-value">{{ authorDisplayName(detail) }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">身份证号</span>
              <span class="field-value">{{ detail.authorIdCard || detail.idCardNo || '-' }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">联系电话</span>
              <span class="field-value">{{ maskPhone(detail.authorPhone || detail.applicantPhone) }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">电子邮箱</span>
              <span class="field-value">{{ authorEmail(detail) }}</span>
            </div>
          </div>
        </section>

        <section class="detail-section">
          <div class="section-head">
            <span class="section-icon"><i class="el-icon-medal" /></span>
            <span>权利人信息</span>
          </div>
          <div class="field-grid">
            <div class="field-item">
              <span class="field-label">权利人类型</span>
              <span class="field-value">{{ rightsHolderType(detail) }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">权利人姓名</span>
              <span class="field-value">{{ rightsHolderName(detail) }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">{{ detail.ownerType === 2 ? '信用代码' : '身份证号' }}</span>
              <span class="field-value">{{ detail.ownerIdCard || detail.idCardNo || '-' }}</span>
            </div>
          </div>
        </section>

        <section class="detail-section">
          <div class="section-head">
            <span class="section-icon"><i class="el-icon-date" /></span>
            <span>创作信息</span>
          </div>
          <div class="field-grid">
            <div class="field-item">
              <span class="field-label">创作完成日期</span>
              <span class="field-value">{{ formatUserDate(detail.createCompletedDate || detail.workCreatedAt) }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">是否首次发表</span>
              <span class="field-value">{{ detail.firstPublished === 1 ? '是' : '否' }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">首次发表日期</span>
              <span class="field-value">{{ detail.firstPublished === 1 ? formatUserDate(detail.firstPublishDate || detail.workPublishTime) : '-' }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">首次发表地点</span>
              <span class="field-value">{{ detail.firstPublished === 1 ? (detail.firstPublishLocation || '-') : '-' }}</span>
            </div>
          </div>
        </section>

        <section v-if="detail.status === 2 && detail.rejectReason" class="detail-section">
          <div class="section-head">
            <span class="section-icon reject"><i class="el-icon-warning-outline" /></span>
            <span>驳回信息</span>
          </div>
          <div class="desc-block reject-block">
            <div class="desc-label">驳回理由</div>
            <div class="desc-content">{{ detail.rejectReason }}</div>
          </div>
        </section>

        <section v-if="detail.status === 1" class="detail-section">
          <div class="section-head">
            <span class="section-icon"><i class="el-icon-upload" /></span>
            <span>版权证书</span>
          </div>
          <div v-if="detail.certificateUrl" class="cert-current">
            <span class="cert-current-label">当前证书</span>
            <el-button type="text" size="mini" @click="previewCertificate(detail.certificateUrl)">查看 / 下载</el-button>
          </div>
          <div class="certificate-upload">
            <CertificateUpload
              v-model="certificateUrl"
              biz-type="copyright"
              button-text="上传证书"
              hint="支持 JPG/PNG/PDF，不超过 10MB，上传后请点击「保存证书」"
            />
          </div>
        </section>
      </div>

      <div v-if="detail" slot="footer" class="detail-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button
          v-if="detail.status === 1"
          type="primary"
          :loading="submitting"
          :disabled="!certificateUrl || certificateUrl === detail.certificateUrl"
          @click="saveCertificate"
        >保存证书</el-button>
        <template v-if="detail.status === 0">
          <el-button type="danger" @click="openReject(detail)">驳回</el-button>
          <el-button type="success" :loading="submitting" @click="approve(detail)">通过</el-button>
        </template>
      </div>
    </el-dialog>

    <el-dialog title="上传版权证书" :visible.sync="certVisible" width="520px" @closed="resetCertForm">
      <div v-if="certRow" class="cert-dialog-body">
        <div class="cert-work-name">{{ certRow.workTitle || `作品${certRow.workId}` }}</div>
        <div class="cert-meta">版权编号：{{ certRow.copyrightNo || '-' }}</div>
        <CertificateUpload
          v-model="certUrl"
          biz-type="copyright"
          button-text="选择证书文件"
          hint="支持 JPG/PNG/PDF，不超过 10MB"
        />
      </div>
      <div slot="footer">
        <el-button @click="certVisible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="submitting"
          :disabled="!certUrl || certUrl === (certRow && certRow.certificateUrl)"
          @click="saveCertificateFromDialog"
        >保存证书</el-button>
      </div>
    </el-dialog>

    <el-dialog title="驳回版权申请" :visible.sync="rejectVisible" width="480px" @closed="resetRejectForm">
      <el-form ref="rejectForm" :model="rejectForm" :rules="rejectRules" label-width="80px">
        <el-form-item label="作品名称">
          <span>{{ rejectForm.workTitle || '-' }}</span>
        </el-form-item>
        <el-form-item label="驳回理由" prop="rejectReason">
          <el-input
            v-model="rejectForm.rejectReason"
            type="textarea"
            :rows="4"
            maxlength="500"
            show-word-limit
            placeholder="请输入驳回理由，将展示给申请人"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="rejectVisible = false">取消</el-button>
        <el-button type="danger" :loading="submitting" @click="submitReject">确认驳回</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getCopyrightList,
  getCopyrightDetail,
  processCopyright,
  uploadCopyrightCertificate,
  getWorkCategories
} from '@/api/admin'
import CertificateUpload from '@/components/admin/CertificateUpload.vue'
import { resolveFileUrl } from '@/utils/fileUrl'
import {
  COPYRIGHT_STATUS,
  COPYRIGHT_AUDIT_STATUS,
  COPYRIGHT_PAY_STATUS,
  workTypeDisplay,
  labelOf,
  optionsOf
} from '@/utils/adminConstants'
import { formatUserDate, maskPhone } from '@/utils/userFormat'
import listPage from '@/mixins/listPage'

export default {
  name: 'CreationsCopyright',
  components: { CertificateUpload },
  mixins: [listPage],
  data() {
    return {
      COPYRIGHT_STATUS,
      COPYRIGHT_AUDIT_STATUS,
      COPYRIGHT_PAY_STATUS,
      workCategoryMap: {},
      filterStatus: null,
      detailVisible: false,
      detailLoading: false,
      detail: null,
      certificateUrl: '',
      certVisible: false,
      certRow: null,
      certUrl: '',
      submitting: false,
      rejectVisible: false,
      rejectForm: {
        id: null,
        workTitle: '',
        rejectReason: ''
      },
      rejectRules: {
        rejectReason: [
          { required: true, message: '请输入驳回理由', trigger: 'blur' },
          { min: 2, message: '驳回理由至少 2 个字', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    statusOptions() {
      return optionsOf(COPYRIGHT_STATUS)
    }
  },
  created() {
    this.loadWorkCategories()
    this.loadList()
  },
  methods: {
    labelOf,
    formatUserDate,
    maskPhone,
    async loadWorkCategories() {
      try {
        const list = await getWorkCategories() || []
        this.workCategoryMap = Object.fromEntries(list.map(c => [c.id, c.name]))
      } catch (e) {
      }
    },
    displayWorkType(value) {
      if (value == null || value === '') return '-'
      if (this.workCategoryMap[value]) return this.workCategoryMap[value]
      return workTypeDisplay(value)
    },
    formatCopyrightCode(id) {
      if (id == null) return '-'
      return `CR${String(id).padStart(3, '0')}`
    },
    formatWorkCode(id) {
      if (id == null) return '-'
      return `W${String(id).padStart(3, '0')}`
    },
    authorDisplayName(row) {
      return row.authorName || row.applicantName || '-'
    },
    authorEmail(row) {
      if (row.applicantPhone) {
        return `${row.applicantPhone}@example.com`
      }
      return '-'
    },
    rightsHolderType(row) {
      if (row.ownerType === 2) return '企业'
      if (row.ownerType === 1) return '个人'
      return row.companyName ? '企业' : '个人'
    },
    rightsHolderName(row) {
      return row.ownerName || row.companyName || row.authorName || row.applicantName || '-'
    },
    copyrightStatusTagType(status) {
      if (status === 1) return 'success'
      if (status === 2) return 'danger'
      if (status === 3) return 'primary'
      return 'warning'
    },
    auditStatusTagType(status) {
      if (status === 1 || status === 3) return 'success'
      if (status === 2) return 'danger'
      return 'warning'
    },
    payStatusTagType(status) {
      return status === 1 ? 'success' : 'warning'
    },
    fetchList(params) {
      return getCopyrightList({
        ...params,
        status: this.filterStatus != null ? this.filterStatus : undefined
      })
    },
    async openDetail(row) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      try {
        const data = await getCopyrightDetail(row.id)
        this.detail = data
        this.certificateUrl = data.certificateUrl || ''
      } catch (e) {
        this.detailVisible = false
      } finally {
        this.detailLoading = false
      }
    },
    handleDetailClosed() {
      this.detail = null
      this.certificateUrl = ''
    },
    approve(row) {
      this.$confirm(`确定通过「${row.workTitle}」的版权申请？`, '审核通过', { type: 'success' })
        .then(async () => {
          this.submitting = true
          try {
            await processCopyright(row.id, { status: 3 })
            this.$message.success('已通过，等待用户支付')
            if (this.detailVisible && this.detail && this.detail.id === row.id) {
              this.detailVisible = false
            }
            this.loadList()
          } finally {
            this.submitting = false
          }
        }).catch(() => {})
    },
    openReject(row) {
      this.rejectForm = {
        id: row.id,
        workTitle: row.workTitle,
        rejectReason: ''
      }
      this.rejectVisible = true
    },
    resetRejectForm() {
      this.rejectForm = { id: null, workTitle: '', rejectReason: '' }
      if (this.$refs.rejectForm) {
        this.$refs.rejectForm.resetFields()
      }
    },
    submitReject() {
      this.$refs.rejectForm.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          await processCopyright(this.rejectForm.id, {
            status: 2,
            rejectReason: this.rejectForm.rejectReason.trim()
          })
          this.$message.success('已驳回')
          this.rejectVisible = false
          if (this.detailVisible && this.detail && this.detail.id === this.rejectForm.id) {
            this.detailVisible = false
          }
          this.loadList()
        } finally {
          this.submitting = false
        }
      })
    },
    async saveCertificate() {
      if (!this.detail || !this.certificateUrl) return
      this.submitting = true
      try {
        await uploadCopyrightCertificate(this.detail.id, this.certificateUrl)
        this.$message.success('证书已保存，用户可在个人中心下载')
        this.detail.certificateUrl = this.certificateUrl
        this.loadList()
      } finally {
        this.submitting = false
      }
    },
    openCertificate(row) {
      this.certRow = { ...row }
      this.certUrl = row.certificateUrl || ''
      this.certVisible = true
    },
    resetCertForm() {
      this.certRow = null
      this.certUrl = ''
    },
    async saveCertificateFromDialog() {
      if (!this.certRow || !this.certUrl) return
      this.submitting = true
      try {
        await uploadCopyrightCertificate(this.certRow.id, this.certUrl)
        this.$message.success('证书已保存，用户可在个人中心下载')
        this.certVisible = false
        if (this.detailVisible && this.detail && this.detail.id === this.certRow.id) {
          this.detail.certificateUrl = this.certUrl
          this.certificateUrl = this.certUrl
        }
        this.loadList()
      } finally {
        this.submitting = false
      }
    },
    previewCertificate(url) {
      const target = resolveFileUrl(url)
      if (target) window.open(target, '_blank')
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.work-thumb {
  width: 56px;
  height: 56px;
  border-radius: 6px;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #c0c4cc;
  font-size: 22px;
}
.work-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.fee {
  color: #e6a23c;
  font-weight: 500;
}
.text-muted {
  color: #c0c4cc;
}
.pagination {
  margin-top: 16px;
  text-align: right;
}
</style>

<style>
.copyright-detail-dialog .el-dialog__header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f0f2f5;
}
.copyright-detail-dialog .el-dialog__body {
  padding: 16px 24px 8px;
  max-height: 70vh;
  overflow-y: auto;
}
.copyright-detail-dialog .el-dialog__footer {
  padding: 12px 24px 20px;
  border-top: 1px solid #f0f2f5;
}
.copyright-detail-dialog .detail-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}
.copyright-detail-dialog .detail-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.copyright-detail-dialog .detail-section {
  padding: 16px 0;
  border-bottom: 1px solid #f0f2f5;
}
.copyright-detail-dialog .detail-section:last-child {
  border-bottom: none;
}
.copyright-detail-dialog .section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}
.copyright-detail-dialog .section-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}
.copyright-detail-dialog .section-icon.reject {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}
.copyright-detail-dialog .field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
}
.copyright-detail-dialog .field-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.copyright-detail-dialog .field-label {
  font-size: 13px;
  color: #94a3b8;
}
.copyright-detail-dialog .field-value {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  word-break: break-all;
}
.copyright-detail-dialog .fee-value {
  color: #10b981;
  font-weight: 600;
}
.copyright-detail-dialog .desc-block {
  margin-top: 12px;
}
.copyright-detail-dialog .desc-label {
  font-size: 13px;
  color: #94a3b8;
  margin-bottom: 8px;
}
.copyright-detail-dialog .desc-content {
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 8px;
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  white-space: pre-wrap;
}
.copyright-detail-dialog .reject-block .desc-content {
  background: #fef2f2;
  color: #b91c1c;
}
.copyright-detail-dialog .certificate-upload {
  padding-top: 4px;
}
.copyright-detail-dialog .cert-current {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 13px;
}
.copyright-detail-dialog .cert-current-label {
  color: #64748b;
}
.cert-dialog-body .cert-work-name {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}
.cert-dialog-body .cert-meta {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 16px;
}
.copyright-detail-dialog .detail-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
